import { forwardRef } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Button — Anthropic Claude Peach uslubidagi primitive.
 * R4 polish: solid coral primary, gradient faqat featured (1-2 joyda),
 * outline va danger variantlar warm-tone.
 *
 * Tokens: --brand-primary, --brand-primary-hover, --brand-primary-active,
 *          --brand-gradient, --brand-glow, --brand-soft, --bg-surface,
 *          --bg-elevated, --border-strong, --danger, --brand-clay,
 *          --text-primary, --text-secondary
 */
const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 rounded-md font-medium",
    "transition-all duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-base)]",
    "disabled:pointer-events-none disabled:opacity-50",
  ),
  {
    variants: {
      variant: {
        primary: cn(
          "bg-[var(--brand-primary)] text-white shadow-[var(--shadow-subtle)]",
          "hover:bg-[var(--brand-primary-hover)]",
          "active:bg-[var(--brand-primary-active)]",
        ),
        secondary: cn(
          "bg-[var(--bg-surface)] border border-[var(--border-strong)] text-[var(--text-primary)]",
          "hover:bg-[var(--bg-elevated)]",
        ),
        ghost: cn(
          "bg-transparent text-[var(--text-secondary)]",
          "hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]",
        ),
        outline: cn(
          "border border-[var(--brand-primary)] text-[var(--brand-primary)] bg-transparent",
          "hover:bg-[var(--brand-soft)]",
        ),
        danger: cn(
          "bg-[var(--danger)] text-white shadow-[var(--shadow-subtle)]",
          "hover:bg-[var(--brand-clay)]",
        ),
        // Faqat 1-2 featured uchun (hero CTA, onboarding)
        gradient: cn(
          "text-white shadow-[var(--brand-glow)]",
          "bg-[image:var(--brand-gradient)] hover:brightness-110 active:brightness-95",
        ),
      },
      size: {
        sm: "h-7 px-2.5 text-xs rounded-md",
        md: "h-9 px-3.5 text-sm rounded-md",
        lg: "h-11 px-5 text-base rounded-lg",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  /** Slot bilan boshqa elementga (masalan, Link) variantni qo'llash */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, asChild, ...props }, ref) => {
    // MUHIM: asChild rejimida Slot `React.Children.count` orqali bir element kutadi.
    // Falsy expression (masalan `false && <span/>` → `false`) ham child sifatida
    // hisoblanib, `Children.only` xato beradi. Shuning uchun asChild=true bo'lsa
    // children'ni o'zgartirmasdan o'tkazamiz; spinner faqat asChild=false da.
    if (asChild) {
      return (
        <Slot
          ref={ref as React.Ref<HTMLButtonElement>}
          className={cn(buttonVariants({ variant, size }), className)}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
            aria-hidden
          />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
